import { PixelStreaming } from '@epicgames-ps/lib-pixelstreamingfrontend-ue5.5'
import { AxiosResponse } from 'axios'
import { AuthedAxiosRequests } from 'features/auth'
import type { ParsedMessage } from 'features/kestrelViewer/utils/messageUtils'
import React, {
  createContext,
  Dispatch,
  RefObject,
  SetStateAction,
  useContext,
} from 'react'
import { Vector2 } from 'three'
import {
  GeckoKestrelModel,
  KVAnnotation,
  KVCameraPose,
  KVCameraView,
  KVDataLayersConfig,
  KVInspectionArea,
  KVInspectionGrid,
  KVMarkup,
  KVMeasurement,
  KVNavigationMode,
  KVPhoto,
  KVPlaneIntersection,
  KVPosition,
  KVPrimitive,
  KVResolutionQuality,
  KVRotation,
  KVScreenshotResult,
  KVUnitType,
  KVUserSettings,
  RolodexEntryPatchJson,
} from 'types'

export interface KVTemperature {
  temperature: number
}

export interface KVScreenshotResultLocal {
  requestId: string
  success: boolean
  width: number
  height: number
  position: string
  rotation: string
  imageBase64: string
}
export interface KVLayerControlPoints {
  temp_f: number
  color_rgba: [number, number, number, number]
}

export interface KVActiveLayer {
  prefix: string
  type: 'rgb' | 'visual' | 'raw'
  opacity?: number
  decode_min_f?: number
  decode_max_f?: number
  visible_min_f?: number
  visible_max_f?: number
  gradient?: {
    control_points: KVLayerControlPoints[]
  }
  isCustom?: boolean
  customId?: string
}

// Data used in JSON message handlers
export interface KestrelViewerMessageCallbackAPI {
  pixelStream: PixelStreaming | undefined
  videoParent: RefObject<HTMLDivElement>
  setSelectedMarkup: Dispatch<SetStateAction<KVMarkup | undefined>>
  mouseTrackerRef: RefObject<Vector2>
  setPopupMenuStartPosition: Dispatch<SetStateAction<Vector2>>
  annotations: KVAnnotation[] | undefined
  setAnnotations: Dispatch<SetStateAction<KVAnnotation[] | undefined>>
  historicalAnnotations: KVAnnotation[] | undefined
  setHistoricalAnnotations: Dispatch<SetStateAction<KVAnnotation[] | undefined>>
  settings: KVUserSettings | undefined
  setSettings: Dispatch<SetStateAction<KVUserSettings>>
  inspectionGridSettings: KVInspectionGrid | undefined
  setInspectionGridSettings: Dispatch<
    SetStateAction<KVInspectionGrid | undefined>
  >
  measurements: KVMeasurement[] | undefined
  setMeasurements: Dispatch<SetStateAction<KVMeasurement[] | undefined>>
  user: { name: string; email: string }
  rolodexPatch: (patchData: RolodexEntryPatchJson[]) => Promise<AxiosResponse>
  kestrelModel: GeckoKestrelModel | undefined
  planePosition?: KVPosition
  setPlanePosition?: Dispatch<SetStateAction<KVPosition | undefined>>
  planeRotation?: KVRotation
  setPlaneRotation?: Dispatch<SetStateAction<KVRotation | undefined>>
  planeIntersections: KVPlaneIntersection[] | undefined
  setPlaneIntersections: Dispatch<
    SetStateAction<KVPlaneIntersection[] | undefined>
  >
  photoReelOpen: boolean
  setPhotoReelOpen: Dispatch<SetStateAction<boolean>>
  selectedPhoto: KVPhoto | null
  setSelectedPhoto: Dispatch<SetStateAction<KVPhoto | null>>
  photos: KVPhoto[]
  setPhotos: Dispatch<SetStateAction<KVPhoto[]>>
  filteredPhotos: KVPhoto[] | null
  setFilteredPhotos: Dispatch<SetStateAction<KVPhoto[] | null>>
  cameraViews: KVCameraView[] | undefined
  setCameraViews: Dispatch<SetStateAction<KVCameraView[] | undefined>>
  navigationMode: KVNavigationMode
  setNavigationMode: Dispatch<SetStateAction<KVNavigationMode>>
  authorizedAxios: AuthedAxiosRequests
  kestrelApiUrl: string
  filteringPhotos: boolean
  setFilteringPhotos: Dispatch<SetStateAction<boolean>>
  resolutionQuality?: KVResolutionQuality
  setResolutionQuality?: Dispatch<SetStateAction<KVResolutionQuality>>
  currentTemperature: KVTemperature | null
  setCurrentTemperature: Dispatch<SetStateAction<KVTemperature | null>>
  temperatureProbeLoading: boolean
  setTemperatureProbeLoading: Dispatch<SetStateAction<boolean>>
  selectedMode: string
  setScreenshotResult?: Dispatch<SetStateAction<KVScreenshotResult | null>>
  nextAnnotationNumber: number
  setNextAnnotationNumber: Dispatch<SetStateAction<number>>
  nextMeasurementNumber: number
  setNextMeasurementNumber: Dispatch<SetStateAction<number>>
  inspectionAreas: KVInspectionArea[] | undefined
  setInspectionAreas: Dispatch<SetStateAction<KVInspectionArea[] | undefined>>
  primitives: KVPrimitive[] | undefined
  setPrimitives: Dispatch<SetStateAction<KVPrimitive[] | undefined>>
  selectedWhiteboardId?: string | null // Optional: only available in SystemView context
  onTextSegmentationComplete?: () => void // Callback to notify when textSegmentation batch is received
}

// Data available to client-side Kestrel Viewer UI
export type KestrelViewerContextType = {
  sendCommand: (json: string | object) => void
  selectedMode: string
  setSelectedMode: Dispatch<SetStateAction<string>>
  selectedMarkup: KVMarkup | undefined
  setSelectedMarkup: Dispatch<SetStateAction<KVMarkup | undefined>>
  popupMenuStartPosition: Vector2
  helperTextVisible: boolean
  setHelperTextVisible: Dispatch<SetStateAction<boolean>>
  settingsOpen: boolean
  setSettingsOpen: Dispatch<SetStateAction<boolean>>
  infoOpen: boolean
  setInfoOpen: Dispatch<SetStateAction<boolean>>
  inspectionGridSettingsOpen: boolean
  setInspectionGridSettingsOpen: Dispatch<SetStateAction<boolean>>
  inspectionGridSettings: KVInspectionGrid | undefined
  setInspectionGridSettings: Dispatch<
    SetStateAction<KVInspectionGrid | undefined>
  >
  settings: KVUserSettings | undefined
  setSettings: Dispatch<SetStateAction<KVUserSettings>>
  is2DPlaneModeActive: boolean
  setIs2DPlaneModeActive: Dispatch<SetStateAction<boolean>>
  planePosition?: KVPosition
  setPlanePosition?: Dispatch<SetStateAction<KVPosition | undefined>>
  planeRotation?: KVRotation
  setPlaneRotation?: Dispatch<SetStateAction<KVRotation | undefined>>
  measurementType: string
  setMeasurementType: Dispatch<SetStateAction<string>>
  planeIntersections: KVPlaneIntersection[] | undefined
  setPlaneIntersections: Dispatch<
    SetStateAction<KVPlaneIntersection[] | undefined>
  >
  cameraViews: KVCameraView[] | undefined
  setCameraViews: Dispatch<SetStateAction<KVCameraView[] | undefined>>
  primitives: KVPrimitive[] | undefined
  setPrimitives: Dispatch<SetStateAction<KVPrimitive[] | undefined>>
  photoReelOpen: boolean
  setPhotoReelOpen: Dispatch<SetStateAction<boolean>>
  selectedPhoto: KVPhoto | null
  setSelectedPhoto: Dispatch<SetStateAction<KVPhoto | null>>
  photos: KVPhoto[]
  setPhotos: Dispatch<SetStateAction<KVPhoto[]>>
  filteredPhotos: KVPhoto[] | null
  setFilteredPhotos: Dispatch<SetStateAction<KVPhoto[] | null>>
  photoRotations: Record<string, number>
  getPhotoRotation: (photoId: string) => number
  setPhotoRotation: (photoId: string, rotation: number) => void
  savePhotoRotations: () => Promise<void>
  photosLoading: boolean
  photosError: Error | null
  containerRef: RefObject<HTMLDivElement | null>
  navigationMode: KVNavigationMode
  setNavigationMode: Dispatch<SetStateAction<KVNavigationMode>>
  kestrelModel: GeckoKestrelModel | undefined
  pixelStreamingURL: string
  setPixelStreamingURL: Dispatch<SetStateAction<string>>
  setSendKVCommand: Dispatch<
    SetStateAction<((json: object | string) => void) | undefined>
  >
  setPopupMenuStartPosition: Dispatch<SetStateAction<Vector2>> | undefined
  cameraPoses: KVCameraPose[] | null
  setCameraPoses: Dispatch<SetStateAction<KVCameraPose[] | null>>
  filteringPhotos: boolean
  setFilteringPhotos: Dispatch<SetStateAction<boolean>>
  resolutionQuality: KVResolutionQuality
  setResolutionQuality: Dispatch<SetStateAction<KVResolutionQuality>>
  thermalGradientVisible: boolean
  setThermalGradientVisible: Dispatch<SetStateAction<boolean>>
  activeLayer: KVActiveLayer
  setActiveLayer: Dispatch<SetStateAction<KVActiveLayer>>
  availableLayerPrefixes: string[]
  setAvailableLayerPrefixes: Dispatch<SetStateAction<string[]>>
  dataLayersConfig: KVDataLayersConfig | undefined
  currentTemperature: KVTemperature | null
  setCurrentTemperature: Dispatch<SetStateAction<KVTemperature | null>>
  temperatureProbeLoading: boolean
  setTemperatureProbeLoading: Dispatch<SetStateAction<boolean>>
  authorizedAxios: AuthedAxiosRequests
  kestrelApiUrl: string
  screenshotResult: KVScreenshotResult | null
  setScreenshotResult: Dispatch<SetStateAction<KVScreenshotResult | null>>
  currentUnit: KVUnitType
  setCurrentUnit: Dispatch<SetStateAction<KVUnitType>>

  dataValidationPanelOpen: boolean
  setDataValidationPanelOpen: Dispatch<SetStateAction<boolean>>

  textSegmentationPopoverVisible: boolean
  setTextSegmentationPopoverVisible: Dispatch<SetStateAction<boolean>>

  updateHistoricalAnnotation: (
    annotation: KVAnnotation,
    jsonField: string | string[],
    setter: (a: KVAnnotation) => KVAnnotation,
    v: unknown | unknown[]
  ) => void

  updateAnnotation: (
    annotation: KVAnnotation,
    jsonField: string | string[],
    setter: (a: KVAnnotation) => KVAnnotation,
    v: unknown | unknown[]
  ) => void

  updateMeasurement: (
    measurement: KVMeasurement,
    jsonField: string | string[],
    setter: (a: KVMeasurement) => KVMeasurement,
    v: string | boolean | (string | boolean)[]
  ) => void

  updateInspectionArea: (
    inspectionArea: KVInspectionArea,
    jsonField: string | string[],
    setter: (ia: KVInspectionArea) => KVInspectionArea,
    v: unknown | unknown[]
  ) => void

  updatePrimitive: (
    primitive: KVPrimitive,
    jsonField: string | string[],
    setter: (p: KVPrimitive) => KVPrimitive,
    v: unknown | unknown[]
  ) => void

  /** Buffer of the most recently received messages from Kestrel (last 100) */
  recentMessages: ParsedMessage[]

  /** Set callback to be notified when textSegmentation batch annotation is received */
  onTextSegmentationComplete?: (callback: () => void) => void
}

export const KestrelViewerContext = createContext<
  KestrelViewerContextType | undefined
>(undefined)

export const KestrelViewerProvider: React.FC<{
  children: React.ReactNode
  value: KestrelViewerContextType
}> = ({ children, value }) => {
  return (
    <KestrelViewerContext.Provider value={value}>
      {children}
    </KestrelViewerContext.Provider>
  )
}

export const useKestrelViewerContext = () => {
  const context = useContext(KestrelViewerContext)
  if (!context)
    throw new Error(
      'useKestrelViewerContext() must be used within KestrelViewerProvider'
    )
  return context
}
